/**
 * BUSINESS KANBAN CARD — adattatore entity-specific della card azienda.
 *
 * Non ridisegna lo scheletro (root/top/titolo/meta/foot): quello è di {@link KbCard}, condiviso con
 * preventivi, task e contatti. Qui si passano solo i dati dell'azienda: avatar con l'iniziale,
 * ragione sociale (link al dettaglio), settore e forma giuridica nel `meta`, i recapiti principali
 * nel `foot`.
 *
 * <b>I recapiti non costano una chiamata.</b> `emailAddresses` e `phones` arrivano già dentro il DTO
 * dell'azienda — il backend li risolve in `toBusiness` — quindi la card legge quello che ha già in
 * mano e non va a cercare nulla.
 *
 * Lo `status` dell'azienda (attiva/inattiva) **non** compare: non è lo stato della bacheca, e
 * metterlo accanto alla colonna suggerirebbe un legame che non c'è. La colonna dice dove sta
 * l'azienda in *questa* pipeline; lo status dice se è un cliente attivo, e vale ovunque.
 */

import { KbCard } from '../../components/KbCard';
import { BusinessAvatar } from '../../components/BusinessAvatar';
import type { Business } from './businessMappers';

export function BusinessKanbanCard({ business }: { business: Business }) {
  const { businessId, name, sector, type, emailAddresses, phones } = business;

  // Il recapito "principale" è quello marcato primary; senza marcatura vale il primo, che è
  // comunque meglio di una card muta. Il `?.` regge le risposte senza collezioni.
  const email = emailAddresses?.find((e) => e.primary)?.address ?? emailAddresses?.[0]?.address;
  const phone = phones?.find((p) => p.primary)?.number ?? phones?.[0]?.number;

  return (
    <KbCard
      lead={<BusinessAvatar name={name} />}
      title={name || `#${businessId}`}
      titleTo={`/companies/${businessId}`}
      draggable
      meta={
        <>
          {sector ? <span className="kb-card__tag">{sector}</span> : <span />}
          {type && <span className="kb-card__tag">{type}</span>}
        </>
      }
      foot={
        email || phone ? (
          <>
            <span className="kb-card__contact">{email ?? ''}</span>
            {phone && <span className="kb-card__contact">{phone}</span>}
          </>
        ) : undefined
      }
    />
  );
}
